const JOYRIDE_GITHUB_REPO = 'Nuosis/joyride';
const FRONTEND_RUNTIME_ERRORS_PATH = '/functions/v1/runtime-errors-frontend';
const DEDUPE_WINDOW_MS = 15_000;

const recentErrorKeys = new Map<string, number>();

export interface FrontendRuntimeErrorPayload {
  message: string;
  title: string;
  url: string;
  userAgent: string;
  correlationId: string;
  githubRepo: string;
  source: 'window.error' | 'window.unhandledrejection' | 'react.errorboundary' | 'manual';
  stack?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
  component?: string;
  extras?: Record<string, unknown>;
}

export function generateCorrelationId(): string {
  return crypto.randomUUID();
}

export function getRepoHeaders(correlationId = generateCorrelationId()): Record<string, string> {
  return {
    'x-devin-repo': JOYRIDE_GITHUB_REPO,
    'x-correlation-id': correlationId,
  };
}

export function buildFrontendRuntimeErrorEndpoint(): string | null {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!supabaseUrl) return null;
  return `${supabaseUrl}${FRONTEND_RUNTIME_ERRORS_PATH}`;
}

function normalizeErrorLike(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) {
    return {
      message: value.message || value.name || 'Unknown error',
      stack: value.stack,
    };
  }

  if (typeof value === 'string') {
    return { message: value };
  }

  try {
    return { message: JSON.stringify(value) };
  } catch {
    return { message: String(value) };
  }
}

function extractTopReactComponent(componentStack?: string): string | undefined {
  if (!componentStack) return undefined;

  const lines = componentStack.split('\n');
  for (const line of lines) {
    const match = line.trim().match(/^at\s+([A-Za-z0-9_.]+)/);
    if (match?.[1]) {
      return match[1];
    }
  }

  return undefined;
}

function shouldReport(key: string): boolean {
  const now = Date.now();
  const lastSeen = recentErrorKeys.get(key);
  if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) {
    return false;
  }
  recentErrorKeys.set(key, now);

  for (const [storedKey, ts] of recentErrorKeys.entries()) {
    if (now - ts > DEDUPE_WINDOW_MS) {
      recentErrorKeys.delete(storedKey);
    }
  }

  return true;
}

export async function reportFrontendRuntimeError(payload: FrontendRuntimeErrorPayload): Promise<void> {
  const endpoint = buildFrontendRuntimeErrorEndpoint();
  if (!endpoint) {
    console.warn('Skipping runtime error reporting: VITE_SUPABASE_URL is not configured');
    return;
  }

  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const dedupeKey = [payload.source, payload.title, payload.message, payload.filename, payload.lineno, payload.colno].join('|');
  if (!shouldReport(dedupeKey)) {
    return;
  }

  try {
    await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apikey ? { apikey } : {}),
        ...getRepoHeaders(payload.correlationId),
      },
      body: JSON.stringify({
        service: 'joyride_frontend',
        title: payload.title,
        message: payload.message,
        stack: payload.stack,
        trace_id: payload.correlationId,
        context: {
          github_repo: payload.githubRepo,
          page_url: payload.url,
          user_agent: payload.userAgent,
          source: payload.source,
          filename: payload.filename,
          lineno: payload.lineno,
          colno: payload.colno,
          component: payload.component,
          extras: payload.extras ?? {},
        },
      }),
      keepalive: true,
    });
  } catch (error) {
    console.error('Failed to report frontend runtime error', error);
  }
}

export async function reportReactErrorBoundaryError(
  error: unknown,
  componentStack?: string
): Promise<void> {
  const normalized = normalizeErrorLike(error);

  await reportFrontendRuntimeError({
    title: 'React component tree error',
    message: normalized.message,
    stack: normalized.stack,
    correlationId: generateCorrelationId(),
    githubRepo: JOYRIDE_GITHUB_REPO,
    source: 'react.errorboundary',
    url: typeof window === 'undefined' ? 'unknown' : window.location.href,
    userAgent: typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent,
    component: extractTopReactComponent(componentStack),
    extras: componentStack ? { componentStack } : undefined,
  });
}

export function installGlobalRuntimeErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  const handleWindowError = (event: ErrorEvent) => {
    const normalized = normalizeErrorLike(event.error ?? event.message);
    void reportFrontendRuntimeError({
      title: event.message || 'Unhandled frontend error',
      message: normalized.message,
      stack: normalized.stack,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      correlationId: generateCorrelationId(),
      githubRepo: JOYRIDE_GITHUB_REPO,
      source: 'window.error',
      url: window.location.href,
      userAgent: navigator.userAgent,
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const normalized = normalizeErrorLike(event.reason);
    void reportFrontendRuntimeError({
      title: 'Unhandled promise rejection',
      message: normalized.message,
      stack: normalized.stack,
      correlationId: generateCorrelationId(),
      githubRepo: JOYRIDE_GITHUB_REPO,
      source: 'window.unhandledrejection',
      url: window.location.href,
      userAgent: navigator.userAgent,
      extras: {
        reasonType: typeof event.reason,
      },
    });
  };

  window.addEventListener('error', handleWindowError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}
